import React, { useEffect, useMemo, useState } from 'react'

import AccountTree from 'components_new/molecules/AccountTree'
import GuideDialog from 'components_new/molecules/GuideDialog'
import SetAllocationPerRealEstate from 'components_new/molecules/SetAllocationPerRealEstate'

import { useAllocationKeyActions } from 'redux/actions/AllocationKeys'
import { useAllocationKeysData } from 'redux/hooks/AllocationKeys'
import { useEconomySetsData } from 'redux/hooks/EconomySets'
import { useRealEstatesData } from 'redux/hooks/RealEstates'

import Actions from './Actions'

import {
  IParsedRealEstateForAllocationKeys,
  parseAllocationBody,
  parseRealEstatesForAllocationKeys
} from 'types/GlobalRealEstate'
import TextField from 'components_new/atoms/TextField'
import { Strings, useTranslation } from 'language/translator'

interface ICreateAllocationKeyDialogProps {
  onClose: () => void
  open: boolean
}

const CreateAllocationKeyDialog = (props: ICreateAllocationKeyDialogProps) => {
  const allocationKeysData = useAllocationKeysData()
  const economySetsData = useEconomySetsData()
  const realEstatesData = useRealEstatesData()
  const { tryCreateAllocationKey } = useAllocationKeyActions()

  const { t } = useTranslation()

  const [allocations, setAllocations] = useState<{
    [key: string]: string
  }>({})
  const [isCreating, setIsCreating] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
  const [step, setStep] = useState(1)

  const disabledAccounts = useMemo(() => {
    return Object.values(allocationKeysData)
      .map((item) => item.accounts)
      .flat()
  }, [allocationKeysData])

  useEffect(() => {
    if (props.open) {
      const initialAllocationKeys: { [key: string]: string } = {}

      Object.values(realEstatesData).forEach((realEstate) => {
        initialAllocationKeys[realEstate.id] = '0'
      })

      setAllocations(initialAllocationKeys)
      setStep(1)
      setNameInput('')
      setSelectedAccounts([])
    }
  }, [props.open])

  const parsedRealEstates = useMemo(
    () => parseRealEstatesForAllocationKeys(Object.values(realEstatesData)),
    [realEstatesData]
  )

  const mappedAccounts = useMemo(() => {
    // A real estate company can only have one economy set.
    const map: { [accountNumber: string]: string } = {}
    const set = Object.values(economySetsData)[0]

    if (set) {
      set.accounts.forEach((acc) => {
        if (acc.name) {
          map[acc.number] = acc.name
        }
      })
    }

    return map
  }, [economySetsData])

  function handleCreate() {
    setIsCreating(true)

    tryCreateAllocationKey(
      {
        name: nameInput,
        accounts: selectedAccounts,
        allocations: Object.entries(allocations).map(([realEstateId, share]) =>
          parseAllocationBody(realEstateId, share)
        )
      },
      (err) => {
        if (!err) {
          props.onClose()
        }

        setIsCreating(false)
      }
    )
  }

  return (
    <GuideDialog
      actions={
        <Actions
          allocations={allocations}
          nameInput={nameInput}
          isCreating={isCreating}
          onClose={props.onClose}
          onCreate={handleCreate}
          selectedAccounts={selectedAccounts}
          step={step}
          setStep={setStep}
        />
      }
      onClose={() => {}}
      open={props.open}
      title={getTitle(step, t)}
      subtitle={getSubtitle(step, t)}
      steps={3}
      step={step}
    >
      <Body
        allocations={allocations}
        disabledAccounts={disabledAccounts}
        setAllocations={setAllocations}
        step={step}
        mappedAccounts={mappedAccounts}
        nameInput={nameInput}
        setNameInput={setNameInput}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        realEstates={parsedRealEstates}
      />
    </GuideDialog>
  )
}

function getTitle(
  step: number,
  t: (namespace: 'allocation_keys', key: Strings<'allocation_keys'>) => string
) {
  if (step === 1) {
    return t('allocation_keys', 'name_key_title')
  }

  if (step === 2) {
    return t('allocation_keys', 'select_accounts_title')
  }

  return t('allocation_keys', 'select_allocation_title')
}

function getSubtitle(
  step: number,
  t: (namespace: 'allocation_keys', key: Strings<'allocation_keys'>) => string
) {
  if (step === 1) {
    return t('allocation_keys', 'name_key_subtitle')
  }

  if (step === 2) {
    return t('allocation_keys', 'select_accounts_subtitle')
  }

  return t('allocation_keys', 'select_allocation_subtitle')
}

const Body = (props: {
  allocations: { [key: string]: string }
  disabledAccounts: string[]
  setAllocations: (keys: { [key: string]: string }) => void
  step: number
  mappedAccounts: { [number: string]: string }
  nameInput: string
  setNameInput: (value: string) => void
  selectedAccounts: string[]
  setSelectedAccounts: (values: string[]) => void
  realEstates: IParsedRealEstateForAllocationKeys[]
}) => {
  const { t } = useTranslation()

  if (props.step === 1) {
    return (
      <TextField
        fullWidth={true}
        label={t('global', 'name')}
        autoComplete="off"
        value={props.nameInput}
        onChange={(event) => props.setNameInput(event.target.value)}
        sx={{ mt: 1, mb: 2 }}
        required
      />
    )
  }

  if (props.step === 2) {
    return (
      <AccountTree
        disabledAccounts={props.disabledAccounts}
        mappedAccounts={props.mappedAccounts}
        selected={props.selectedAccounts}
        setSelected={props.setSelectedAccounts}
      />
    )
  }

  return (
    <SetAllocationPerRealEstate
      allocations={props.allocations}
      setAllocations={props.setAllocations}
      realEstates={props.realEstates}
    />
  )
}

export default CreateAllocationKeyDialog
